// src/features/dashboard/TeacherDashboard.jsx
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useAuth } from '../../hooks/useAuth';
import {
  fetchTeacherClasses,
  fetchAllUsers,
  bulkAddStudents,
  updateTutoringStudent,
} from "../../services/sheetDataService";
import ClassManagement from './teacher/ClassManagement';
import AddStudentsSection from './teacher/AddStudentsSection';
import TutorContent from './admin/tutor/TutorContent';
import AddTutoringStudentForm from './admin/tutor/AddTutoringStudentForm';

const TeacherDashboard = () => {
  const { user, accessToken } = useAuth();

  // Extract stable primitive values from user
  const userEmail = user?.email || '';
  const userName = user?.name || '';
  const userSurname = user?.surname || '';
  const spreadsheetId = user?.spreadsheetId || '';
  const usersSheetId = user?.usersSpreadsheetId || spreadsheetId;
  const attendanceSheetId = user?.attendanceSpreadsheetId || spreadsheetId;
  const marksSheetId = user?.marksSpreadsheetId || spreadsheetId;
  const organisationId = user?.organisationId || '';
  const school = user?.school || '';
  const province = user?.province || '';
  const district = user?.district || '';
  const circuit = user?.circuit || '';

  const [viewMode, setViewMode] = useState('teacher');

  // Teacher mode state
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState(null);
  const [loadingClasses, setLoadingClasses] = useState(true);
  const [showNewClassForm, setShowNewClassForm] = useState(false);
  const [newClassDetails, setNewClassDetails] = useState({
    subject: '',
    grade: '',
    googleClassroomLink: '',
    schedule: [{ day: '', startTime: '', duration: '' }],
    zoomLink: '',
    zoomPassword: '',
  });

  // Tutor mode state
  const [students, setStudents] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [showAddStudentForm, setShowAddStudentForm] = useState(false);

  const selectedUserIdRef = useRef(null);

  useEffect(() => {
    selectedUserIdRef.current = selectedStudent?.userId || null;
  }, [selectedStudent]);

  const loadClasses = useCallback(async () => {
    if (!spreadsheetId || !accessToken || !userEmail) {
      setLoadingClasses(false);
      return;
    }
    setLoadingClasses(true);
    try {
      const teacherClasses = await fetchTeacherClasses(
        accessToken,
        usersSheetId,
        userEmail,
        `${userName} ${userSurname}`
      );
      setClasses(teacherClasses);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingClasses(false);
    }
  }, [accessToken, usersSheetId, userEmail, userName, userSurname, spreadsheetId]);

  const loadStudents = useCallback(async () => {
    if (!spreadsheetId || !accessToken || !userEmail) {
      setLoadingStudents(false);
      return;
    }

    const userIdToKeep = selectedUserIdRef.current;

    setLoadingStudents(true);
    try {
      const allUsers = await fetchAllUsers(accessToken, usersSheetId);
      const tutorRows = allUsers.filter(u =>
        u.role === 'Student' &&
        u.teacher === userEmail &&
        u.studentType === 'tutor'
      );

      const studentMap = new Map();
      tutorRows.forEach(row => {
        const key = row.email.toLowerCase().trim();
        if (!studentMap.has(key)) {
          studentMap.set(key, { ...row, subjects: [row] });
        } else {
          studentMap.get(key).subjects.push(row);
        }
      });
      const groupedStudents = Array.from(studentMap.values());
      setStudents(groupedStudents);

      if (userIdToKeep) {
        const updatedSelected = groupedStudents.find(s => s.userId === userIdToKeep);
        setSelectedStudent(updatedSelected || (groupedStudents.length > 0 ? groupedStudents[0] : null));
      } else if (groupedStudents.length > 0 && !selectedStudent) {
        setSelectedStudent(groupedStudents[0]);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingStudents(false);
    }
  }, [accessToken, usersSheetId, userEmail, spreadsheetId]);

  useEffect(() => {
    if (classes.length > 0 && !selectedClass) {
      setSelectedClass(classes[0]);
    }
  }, [classes, selectedClass]);

  useEffect(() => {
    if (viewMode === 'teacher') {
      loadClasses();
    } else {
      loadStudents();
    }
  }, [viewMode, loadClasses, loadStudents]);

  const handleNewClassDetailsChange = useCallback((field, value) => {
    setNewClassDetails(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleAddStudentsToNewClass = useCallback(async (students, classDetails) => {
    const metadata = {
      organisationId,
      school,
      province,
      district,
      circuit,
      teacher: userEmail,
      subject: classDetails.subject,
      grade: classDetails.grade,
      googleClassroomLink: classDetails.googleClassroomLink || '',
      schedule: classDetails.schedule || [],
      zoomLink: classDetails.zoomLink || '',
      zoomPassword: classDetails.zoomPassword || '',
      studentType: 'group',
    };

    try {
      await bulkAddStudents(accessToken, usersSheetId, students, metadata);
      await loadClasses();
      setShowNewClassForm(false);
      setNewClassDetails({
        subject: '',
        grade: '',
        googleClassroomLink: '',
        schedule: [{ day: '', startTime: '', duration: '' }],
        zoomLink: '',
        zoomPassword: '',
      });
      alert('✅ Class and students created successfully!');
    } catch (err) {
      alert('❌ ' + (err.message || 'Failed to create class.'));
    }
  }, [accessToken, usersSheetId, userEmail, organisationId, school, province, district, circuit, loadClasses]);

  const handleCancelNewClass = useCallback(() => {
    setShowNewClassForm(false);
    setNewClassDetails({
      subject: '',
      grade: '',
      googleClassroomLink: '',
      schedule: [{ day: '', startTime: '', duration: '' }],
      zoomLink: '',
      zoomPassword: '',
    });
  }, []);

  const handleAddTutorStudent = useCallback(async (studentData) => {
    const metadata = {
      organisationId,
      school,
      province,
      district,
      circuit,
      teacher: userEmail,
      subject: studentData.subject,
      grade: studentData.grade,
      googleClassroomLink: studentData.googleClassroomLink || '',
      schedule: studentData.schedule || [],
      zoomLink: studentData.zoomLink || '',
      zoomPassword: studentData.zoomPassword || '',
      studentType: 'tutor',
      gender: studentData.gender || '',
    };

    const studentsArray = [{
      name: studentData.name,
      surname: studentData.surname,
      email: studentData.email,
      gender: studentData.gender || '',
    }];

    try {
      await bulkAddStudents(accessToken, usersSheetId, studentsArray, metadata);
      await loadStudents();
      setShowAddStudentForm(false);
      alert('✅ Tutoring student added successfully!');
    } catch (err) {
      alert('❌ Failed to add student: ' + err.message);
    }
  }, [accessToken, usersSheetId, userEmail, organisationId, school, province, district, circuit, loadStudents]);

  const handleEditTutorStudent = useCallback(async (updatedData) => {
    try {
      await updateTutoringStudent(
        accessToken,
        usersSheetId,
        selectedStudent?.userId,
        updatedData,
        attendanceSheetId,
        marksSheetId
      );
      await loadStudents();
      alert('✅ Student updated successfully!');
    } catch (err) {
      alert('❌ Failed to update student: ' + err.message);
    }
  }, [accessToken, usersSheetId, selectedStudent, attendanceSheetId, marksSheetId, loadStudents]);

  const isLoading = (viewMode === 'teacher' && loadingClasses) || (viewMode === 'tutor' && loadingStudents);
  if (isLoading) {
    return <div className="text-center py-10">Loading your dashboard...</div>;
  }

  return (
    <div className="max-w-7xl mx-auto py-6 px-4">
      {/* Header with toggle */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">
          {viewMode === 'teacher' ? 'Teacher Dashboard' : 'Tutor Dashboard'}
        </h1>
        <div className="flex items-center gap-2">
          <span className={`text-sm ${viewMode === 'teacher' ? 'font-bold' : 'text-gray-500'}`}>Group</span>
          <button
            onClick={() => setViewMode(viewMode === 'teacher' ? 'tutor' : 'teacher')}
            className={`w-12 h-6 rounded-full p-1 transition-colors ${viewMode === 'teacher' ? 'bg-blue-600' : 'bg-gray-300'}`}
          >
            <div
              className={`w-4 h-4 bg-white rounded-full transition-transform ${viewMode === 'teacher' ? 'translate-x-6' : 'translate-x-0'}`}
            />
          </button>
          <span className={`text-sm ${viewMode === 'tutor' ? 'font-bold' : 'text-gray-500'}`}>Tutor</span>
        </div>
      </div>

      <div className="flex flex-col md:flex-row gap-4">
        {/* Sidebar */}
        <div className="md:w-1/4 bg-white p-4 rounded shadow">
          {viewMode === 'teacher' ? (
            <>
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold">My Classes</h2>
                {classes.length > 0 && (
                  <button
                    onClick={() => setShowNewClassForm(true)}
                    className="text-sm bg-blue-600 text-white px-2 py-1 rounded hover:bg-blue-700"
                  >
                    + New
                  </button>
                )}
              </div>
              {classes.length === 0 ? (
                <p className="text-gray-500">No classes yet.</p>
              ) : (
                <ul>
                  {classes.map(cls => (
                    <li
                      key={cls.rowNumber}
                      onClick={() => {
                        setSelectedClass(cls);
                        setShowNewClassForm(false);
                      }}
                      className={`p-2 rounded cursor-pointer ${selectedClass?.rowNumber === cls.rowNumber && !showNewClassForm
                          ? 'bg-blue-100 text-blue-700'
                          : 'hover:bg-gray-100'
                        }`}
                    >
                      {cls.subject} - {cls.grade}
                    </li>
                  ))}
                </ul>
              )}
              {classes.length === 0 && (
                <button
                  onClick={() => setShowNewClassForm(true)}
                  className="mt-3 w-full bg-green-600 text-white px-3 py-2 rounded hover:bg-green-700"
                >
                  + Create First Class
                </button>
              )}
            </>
          ) : (
            <>
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold">My Students</h2>
                <button
                  onClick={() => {
                    setShowAddStudentForm(true);
                    setSelectedStudent(null);
                  }}
                  className="text-sm bg-blue-600 text-white px-2 py-1 rounded hover:bg-blue-700"
                >
                  + Add
                </button>
              </div>
              {students.length === 0 ? (
                <p className="text-gray-500">No students yet.</p>
              ) : (
                <ul>
                  {students.map(student => (
                    <li
                      key={student.userId}
                      onClick={() => {
                        setSelectedStudent(student);
                        setShowAddStudentForm(false);
                      }}
                      className={`p-2 rounded cursor-pointer ${selectedStudent?.userId === student.userId
                          ? 'bg-blue-100 text-blue-700'
                          : 'hover:bg-gray-100'
                        }`}
                    >
                      {student.name} {student.surname}
                      <span className="text-xs text-gray-500 block">
                        {student.subjects.map(s => s.subject).join(', ')}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </div>

        {/* Main content */}
        <div className="md:w-3/4">
          {viewMode === 'teacher' ? (
            showNewClassForm ? (
              <div className="bg-white rounded shadow p-4">
                <h2 className="text-xl font-semibold mb-4">Create New Class</h2>
                <AddStudentsSection
                  mode="newClass"
                  classDetails={newClassDetails}
                  onClassDetailsChange={handleNewClassDetailsChange}
                  onStudentsAdded={handleAddStudentsToNewClass}
                  onCancel={handleCancelNewClass}
                  teacherName={`${userName} ${userSurname}`}
                  teacherEmail={userEmail}
                  organisationId={organisationId}
                  school={school}
                  province={province}
                  district={district}
                  circuit={circuit}
                />
              </div>
            ) : classes.length === 0 ? (
              <div className="bg-white rounded shadow p-4 text-center text-gray-500">
                <p>You have no classes yet.</p>
                <p className="text-sm">Click "Create First Class" to get started.</p>
              </div>
            ) : (
              selectedClass && (
                <ClassManagement
                  classItem={selectedClass}
                  onClassUpdated={loadClasses}
                />
              )
            )
          ) : (
            <>
              {showAddStudentForm ? (
                <div className="bg-white rounded shadow p-4">
                  <h2 className="text-xl font-bold mb-4">Add Tutoring Student</h2>
                  <AddTutoringStudentForm
                    onCancel={() => setShowAddStudentForm(false)}
                    onAdd={handleAddTutorStudent}
                  />
                </div>
              ) : (
                <TutorContent
                  selectedStudent={selectedStudent}
                  students={students}
                  onStudentUpdated={loadStudents}
                  onEditStudent={handleEditTutorStudent}
                />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default TeacherDashboard;